import { APPLICATION_STATUS } from '../utils/Constants';

type ApplicationStatusEntry =
  (typeof APPLICATION_STATUS)[keyof typeof APPLICATION_STATUS];

export type ApplicationStatusKey = number | string | null | undefined;

export type ApplicationStatusLike =
  | ApplicationStatusKey
  | {
      id?: number | string | null;
      code?: string | null;
    };

export const resolveApplicationStatusEntry = (
  statusKey: ApplicationStatusKey
): ApplicationStatusEntry | undefined => {
  if (statusKey === null || statusKey === undefined || statusKey === '') {
    return undefined;
  }

  const numericKey =
    typeof statusKey === 'number' ? statusKey : Number(statusKey);

  if (!Number.isNaN(numericKey) && String(statusKey).trim() !== '') {
    const asString = String(statusKey).trim();
    const looksNumeric = /^-?\d+$/.test(asString) || typeof statusKey === 'number';
    if (looksNumeric) {
      const byNumericCode = Object.values(APPLICATION_STATUS).find(
        (item) => item.code === numericKey
      );
      if (byNumericCode) {
        return byNumericCode;
      }
    }
  }

  const normalizedLabel = String(statusKey).toUpperCase();
  return Object.values(APPLICATION_STATUS).find(
    (item) => item.label === normalizedLabel
  );
};

export const resolveApplicationStatusFromLike = (
  value: ApplicationStatusLike
): ApplicationStatusEntry | undefined => {
  if (value !== null && typeof value === 'object') {
    if (value.code) {
      return resolveApplicationStatusEntry(value.code);
    }
    if (value.id !== null && value.id !== undefined && value.id !== '') {
      return resolveApplicationStatusEntry(value.id);
    }
    return undefined;
  }
  return resolveApplicationStatusEntry(value);
};

/** Prefer API string code (FIN/REJ/…); fall back to legacy numeric id. */
export const getApplicationStatusKey = (
  value: ApplicationStatusLike
): number | string => {
  if (value !== null && typeof value === 'object') {
    if (value.code) {
      return String(value.code).toUpperCase();
    }
    if (value.id !== null && value.id !== undefined && value.id !== '') {
      return value.id;
    }
    return '';
  }
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string' && !/^-?\d+$/.test(value.trim())) {
    return value.toUpperCase();
  }
  return value;
};

export const matchesApplicationStatus = (
  value: ApplicationStatusLike,
  entry: ApplicationStatusEntry
): boolean => resolveApplicationStatusFromLike(value)?.label === entry.label;

export const isRevisionStatus = (value: ApplicationStatusLike) =>
  matchesApplicationStatus(value, APPLICATION_STATUS.revision);

export const isApprovedStatus = (value: ApplicationStatusLike) =>
  matchesApplicationStatus(value, APPLICATION_STATUS.approved);

export const isRejectedStatus = (value: ApplicationStatusLike) =>
  matchesApplicationStatus(value, APPLICATION_STATUS.rejected);

export const isPendingStatus = (value: ApplicationStatusLike) =>
  matchesApplicationStatus(value, APPLICATION_STATUS.pending);

export const isCommitteeStatus = (value: ApplicationStatusLike) =>
  matchesApplicationStatus(value, APPLICATION_STATUS.committee);

export const isInstrumentationStatus = (value: ApplicationStatusLike) =>
  matchesApplicationStatus(value, APPLICATION_STATUS.instrumentation);

export const isReadyToInstrumentStatus = (value: ApplicationStatusLike) =>
  matchesApplicationStatus(value, APPLICATION_STATUS.readyToInstrument);

export const isFinishedStatus = (value: ApplicationStatusLike) =>
  matchesApplicationStatus(value, APPLICATION_STATUS.finished);

export const isFinishedOrRejectedStatus = (value: ApplicationStatusLike) =>
  isFinishedStatus(value) || isRejectedStatus(value);

export const isFinalApplicationStatus = (value: ApplicationStatusLike) =>
  isRejectedStatus(value) ||
  isApprovedStatus(value) ||
  isCommitteeStatus(value) ||
  isInstrumentationStatus(value) ||
  isReadyToInstrumentStatus(value) ||
  isFinishedStatus(value);

const BADGE_BY_LABEL: Record<string, string> = {
  [APPLICATION_STATUS.committee.label]: 'success',
  [APPLICATION_STATUS.approved.label]: 'success',
  [APPLICATION_STATUS.rejected.label]: 'danger',
  [APPLICATION_STATUS.pending.label]: 'warning',
  [APPLICATION_STATUS.revision.label]: 'info',
  [APPLICATION_STATUS.instrumentation.label]: 'success',
  [APPLICATION_STATUS.readyToInstrument.label]: 'success',
  [APPLICATION_STATUS.finished.label]: 'neutral',
};

const TOOLTIP_BY_LABEL: Record<string, string> = {
  [APPLICATION_STATUS.instrumentation.label]: 'En instrumentación',
  [APPLICATION_STATUS.readyToInstrument.label]: 'Para instrumentar',
  [APPLICATION_STATUS.finished.label]: 'CERRADA',
  [APPLICATION_STATUS.approved.label]: 'En análisis de riesgo',
  [APPLICATION_STATUS.rejected.label]: '',
  [APPLICATION_STATUS.pending.label]: 'Comercial pide cambios',
  [APPLICATION_STATUS.revision.label]: 'Documentación en revisión',
  [APPLICATION_STATUS.committee.label]: 'En evaluación de comité',
};

export const getBadgeStatus = (statusCode: number | string) => {
  const status = resolveApplicationStatusEntry(statusCode);
  if (!status) {
    return 'neutral';
  }
  return BADGE_BY_LABEL[status.label] || 'neutral';
};

export const getTooltipText = (statusCode: number | string) => {
  const status = resolveApplicationStatusEntry(statusCode);
  if (!status) {
    return '';
  }
  return TOOLTIP_BY_LABEL[status.label] ?? '';
};

export const getStatusText = (
  statusCode: number | string,
  fallbackText?: string
): string => {
  const status = resolveApplicationStatusEntry(statusCode);
  if (status) {
    return status.text;
  }

  if (fallbackText) {
    const nameLower = fallbackText.toLowerCase();
    if (nameLower === 'rechazada' || nameLower === 'rechazadas') {
      return APPLICATION_STATUS.rejected.text;
    }
    if (nameLower === 'aprobada' || nameLower === 'aceptada') {
      return APPLICATION_STATUS.approved.text;
    }
    if (nameLower === 'cerrada' || nameLower === 'cerradas') {
      return APPLICATION_STATUS.finished.text;
    }
    return fallbackText;
  }
  return 'Estado desconocido';
};
